import { useId, useMemo, useState, type ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { IMPORT_MAX_BYTES, templateCsv, type ImportEntity } from '@quire/shared';
import { ApiError, api } from '../lib/api';
import { IMPORT_COPY, describeImport, importAction, problemsIntro } from '../lib/importCopy';
import { ErrorNote } from './ui';

/**
 * The bulk import panel: get CSV into the box, read what it would do, press
 * Import.
 *
 * A disclosure below the page heading, like the add forms, not a dialog. The
 * box is the one source of truth. Uploading a file fills it and checks it at
 * once, because the check is the answer to "what is in this file": the
 * counts, or the list of lines to fix. Pasting fills it and Check checks it.
 * An edit withdraws any result until the text is checked again, so Import can
 * never send anything but what is on screen. Pressing it sends the same text
 * with `commit: true`; the server plans it afresh, so a file whose world
 * changed in between is refused the same way as a bad one.
 *
 * Everything the panel has to say is said inside the panel, in the section
 * the person is looking at, as the rest of the app does. No toast.
 */
export function CsvImport({
  entity,
  onImported,
  onClose,
}: {
  entity: ImportEntity;
  /** Called once a commit has written. The page invalidates what it shows here. */
  onImported: () => Promise<unknown> | void;
  /** The Done button, once there is nothing left to do. */
  onClose: () => void;
}) {
  const copy = IMPORT_COPY[entity];
  const headingId = useId();
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const preview = useMutation({
    mutationFn: (csv: string) => api.admin.importCsv(entity, { csv, commit: false }),
  });
  const commit = useMutation({
    mutationFn: (csv: string) => api.admin.importCsv(entity, { csv, commit: true }),
    onSuccess: async (result) => {
      if (result.committed) await onImported();
    },
  });

  const template = useMemo(() => templateCsv(entity), [entity]);
  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(template)}`;
  // The template doubles as the placeholder: a sample value, as the other
  // fields' placeholders are, rather than an instruction.
  const placeholder = template.replaceAll('\r\n', '\n').trimEnd();

  const startOver = (): void => {
    preview.reset();
    commit.reset();
    setLocalError(null);
  };

  const choose = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    // Cleared before anything else, so choosing the same file again after
    // fixing it fires a change event rather than being ignored as "no change".
    // The name is kept by hand for the same reason: the input no longer has it.
    input.value = '';
    if (!file) return;
    startOver();
    if (file.size > IMPORT_MAX_BYTES) {
      setLocalError('That file is larger than 1 MiB. Split it and import each part.');
      return;
    }
    let csv: string;
    try {
      csv = await file.text();
    } catch {
      setLocalError('Could not read that file.');
      return;
    }
    // Only now. A refused file leaves the box, and its label, as they were:
    // the old text under the new name would be a lie about what Import sends.
    setText(csv);
    setFileName(file.name);
    preview.mutate(csv);
  };

  const check = (): void => {
    // The server's cap is in code points; UTF-16 units are never fewer, so
    // what passes here passes there, and a paste over it never leaves the tab.
    if (text.length > IMPORT_MAX_BYTES) {
      startOver();
      setLocalError('That is more than 1 MiB of text. Split it and import each part.');
      return;
    }
    preview.mutate(text);
  };

  const edit = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setText(event.currentTarget.value);
    startOver();
    // Once edited it is no longer that file, whatever it started as.
    setFileName(null);
  };

  const busy = preview.isPending || commit.isPending;
  const blank = text.trim() === '';
  const shown = commit.data ?? preview.data;
  const done = commit.data?.committed === true;
  const failure = commit.error ?? preview.error;
  const error =
    localError ??
    (failure
      ? failure instanceof ApiError
        ? failure.message
        : commit.error
          ? 'Could not import that.'
          : 'Could not check that.'
      : null);
  const action = shown && !done ? importAction(shown) : null;

  return (
    <section className="panel mb-5 p-5" aria-labelledby={headingId}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p id={headingId} className="eyebrow">
          Bulk import
        </p>
        {done ? (
          <button type="button" className="btn" onClick={onClose}>
            Done
          </button>
        ) : null}
      </div>
      <p className="mb-3 max-w-2xl text-sm text-ink-soft">
        {copy.intro} Paste it below, or upload a file.{' '}
        <a className="underline" download={`${entity}.csv`} href={templateHref}>
          Download a template
        </a>
        .
      </p>
      <label className="grid gap-1.5">
        <span className="eyebrow">CSV</span>
        <textarea
          className="field h-auto min-h-40 resize-y py-2 font-mono"
          value={text}
          placeholder={placeholder}
          disabled={busy}
          spellCheck={false}
          onChange={edit}
        />
      </label>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* A label dressed as a button, around a visually hidden file input.
            The input keeps the focus and the disabled state; styles.css
            carries both onto the label. `relative` because sr-only is
            absolutely positioned and would otherwise escape to the page edge
            (see Ledger.tsx). */}
        <label className="btn relative">
          <UploadIcon />
          Upload a file
          <input
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            disabled={busy}
            onChange={(event) => void choose(event)}
          />
        </label>
        <button type="button" className="btn" disabled={busy || blank} onClick={check}>
          {preview.isPending ? 'Checking…' : 'Check'}
        </button>
        {fileName ? <span className="font-mono text-xs text-ink-soft">{fileName}</span> : null}
      </div>

      {error ? (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {shown && shown.problems.length > 0 ? (
        <div className="mt-3">
          <p className="text-sm text-ink-soft">{problemsIntro(shown.problems.length)}</p>
          <ul className="mt-2 max-h-72 overflow-auto text-sm">
            {shown.problems.map((problem) => (
              <li key={`${problem.line}:${problem.message}`}>
                Line {problem.line}: {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {shown && shown.problems.length === 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p role="status" className="text-sm">
            {describeImport(shown)}
          </p>
          {action ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => commit.mutate(text)}
            >
              {commit.isPending ? 'Importing…' : action}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * An arrow rising into a tray: the conventional "upload" mark. Drawn, not a
 * glyph, for the reason the theme icons are (UserMenu), and stroked like the
 * drawer's menu icon so the two read as one family.
 */
function UploadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 13V4M6 8l4-4 4 4M4 16h12" />
    </svg>
  );
}
